import './RadioButton.scss'

import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'

import PropTypes from 'prop-types'
import classNames from 'classnames'
import { formEvent } from '../Form/Form'
import t from '../../helpers/i18n'

const KEY_ENTER = 13
const KEY_SPACE = 32
const KEY_UP = 38
const KEY_DOWN = 40
const KEY_LEFT = 37
const KEY_RIGHT = 39

const RadioButton = ({ value, label, initialValue, disabled, highlighted, tabIndex }) => {
  const [selected, setSelected] = useState(initialValue === value)
  const elementRef = useRef()

  const handleClick = useCallback(() => setSelected(true), [])
  const gainFocus = useCallback(() => elementRef.current.focus(), [])

  const handleFocus = useCallback(() => {
    elementRef.current.dispatchEvent(radioButtonEvent({
      action: 'FOCUS',
      value
    }))
  }, [value])

  useEffect(() => {
    elementRef.current.dispatchEvent(radioButtonEvent({
      action: 'REGISTER',
      value,
      update: setSelected,
      focus: gainFocus
    }))
  }, [value, gainFocus])

  useEffect(() => {
    if (selected) {
      elementRef.current.dispatchEvent(radioButtonEvent({
        action: 'SELECT',
        value
      }))
    }
  }, [selected, value])

  return (
    <div
      tabIndex={tabIndex || "0"}
      onFocus={handleFocus}
      onClick={handleClick}
      ref={elementRef}
      className={classNames("radio-button", {
        "-disabled": disabled,
        "-selected": selected
      })}  >
      <div className={classNames("-square")} />
      <div className="-band"></div>
      <div className="-label">
        {label &&
          <label className={classNames("text-label", "color-text", {
            "label-field-sized-basic": !highlighted && !selected,
            "label-field-sized-medium": !highlighted && selected,
            "overline-label-large-bold": highlighted
          })}>
            {label}
          </label>
        }
      </div>
    </div>
  )
}

const RadioButtonGroup = ({ label, name, children, labelPosition, disabled, orientation, required }) => {
  const groupRef = useRef()
  const items = useRef({})
  const currentFocus = useRef()
  const [value, setValue] = useState()

  const handleSelect = useCallback((value) => {
    Object.keys(items.current).forEach(key => {
      if (key !== value) {
        items.current[key].update(false)
      }
    })
    setValue(value)
  }, [])

  const handleRegister = useCallback((value, update, focus) => {
    items.current[value] = { update, focus }
  }, [])

  const handleFocus = useCallback((value) => {
    currentFocus.current = value
  }, [])

  const handleKeyDown = useCallback((event) => {
    const itemValues = Object.keys(items.current)
    const index = itemValues.indexOf(currentFocus.current)
    switch (event.keyCode) {
      case KEY_ENTER:
      case KEY_SPACE:
        itemValues.forEach((value, i) => {
          items.current[itemValues[i]].update(value === currentFocus.current)
        })
        break;
      case KEY_DOWN:
      case KEY_RIGHT:
        if (index + 1 < itemValues.length) {
          items.current[itemValues[index + 1]].focus()
        }
        event.preventDefault()
        break;
      case KEY_UP:
      case KEY_LEFT:
        if (index - 1 >= 0) {
          items.current[itemValues[index - 1]].focus()
        }
        break;
      default:
    }
  }, [])

  useLayoutEffect(() => {
    const eventListener = (event) => {
      if (event.detail.action === 'SELECT') {
        handleSelect(event.detail.value)
      } else if (event.detail.action === 'REGISTER') {
        handleRegister(event.detail.value, event.detail.update, event.detail.focus)
      } else if (event.detail.action === 'FOCUS') {
        handleFocus(event.detail.value)
      }
    }

    const element = groupRef.current
    element.addEventListener('RadioButtonEvent', eventListener, false)
    return () => {
      element.removeEventListener('RadioButtonEvent', eventListener, false)
    }
  }, [handleRegister, handleSelect, handleFocus])

  useEffect(() => {
    if (name) {
      groupRef.current.dispatchEvent(
        formEvent({
          action: 'UPDATE',
          name,
          value
        })
      )
    }
  }, [name, value])

  useEffect(() => {
    if (name) {
      groupRef.current.dispatchEvent(
        formEvent({
          action: 'REQUIRED',
          name,
          required,
        })
      )
    }
  }, [name, required])

  useEffect(() => {
    const element = groupRef.current
    return () => {
      if (name) {
        element.dispatchEvent(
          formEvent({
            action: 'REMOVE',
            name,
          })
        )
      }
    }
  }, [name])

  return (
    <div className="radio-button-container">
      {label && <div className="-label label-small-medium">{label}</div>}
      <div
        onKeyDown={handleKeyDown}
        disabled={disabled === true ? "disabled" : ""}
        ref={groupRef}
        className={classNames("-container", {
          "-vertical": orientation === 'vertical',
          '-inverted': labelPosition === 'left',
          "-disabled": disabled
        })}>
        {children}
      </div>
      {!value && required && !disabled && (
        <span className={classNames('text-label', 'color-error', 'caption-tiny-basic')}>{t('validations.Required')}</span>
      )}
    </div>
  )
}

const radioButtonEvent = ({ action, ...extraData }) => {
  return new CustomEvent('RadioButtonEvent', {
    bubbles: true,
    cancelable: true,
    detail: {
      action,
      ...extraData
    }
  })
}

RadioButton.propTypes = {
  value: PropTypes.string,
  label: PropTypes.string,
  disabled: PropTypes.bool,
  highlighted: PropTypes.bool,
  tabIndex: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
}

RadioButton.defaultProps = {
  disabled: false,
  highlighted: false
}

RadioButtonGroup.propTypes = {
  name: PropTypes.string,
  children: PropTypes.any,
  labelPosition: PropTypes.oneOf(['right', 'left']),
  orientation: PropTypes.oneOf(['vertical', 'horizontal'])
}

RadioButtonGroup.defaultProps = {
  labelPosition: 'right',
  orientation: 'vertical'
}

export default RadioButton
export { RadioButtonGroup }